import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Collapse,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore, Insights } from "@mui/icons-material";
import { guestOrderService, RequestSourcesSummary } from "../../services/guestOrderService";
import { formatCurrencyBDT } from "../../utils/currency";

const PERIODS = [7, 30, 90] as const;

/** Per source/campaign: requests, approved, approved value — with a total
 * row. Shared with Shop Analytics. */
export const SourcesTable: React.FC<{ data: Omit<RequestSourcesSummary, "days"> }> = ({ data }) => (
    <Table size="small" sx={{ "& td, & th": { px: { xs: 1, sm: 2 } } }}>
        <TableHead>
            <TableRow>
                <TableCell sx={{ minWidth: 130 }}>Source / campaign</TableCell>
                <TableCell align="right">Requests</TableCell>
                <TableCell align="right">Approved</TableCell>
                <TableCell align="right">
                    <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Approved value</Box>
                    <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>Value</Box>
                </TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {data.items.map((g) => (
                <TableRow key={`${g.source}|${g.campaign ?? ""}`}>
                    <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="body2" fontWeight={g.paid ? 600 : 400}>
                            {g.source}
                        </Typography>
                        {g.campaign && (
                            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                                {g.campaign}
                            </Typography>
                        )}
                    </TableCell>
                    <TableCell align="right">{g.requests}</TableCell>
                    <TableCell align="right">
                        {g.approved}
                        {g.requests > 0 && (
                            <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: { xs: "none", sm: "inline" } }}
                            >
                                {" "}
                                ({Math.round((g.approved / g.requests) * 100)}%)
                            </Typography>
                        )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {formatCurrencyBDT(g.approved_revenue)}
                    </TableCell>
                </TableRow>
            ))}
            <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>All sources</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {data.total_requests}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {data.total_approved}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {formatCurrencyBDT(data.total_approved_revenue)}
                </TableCell>
            </TableRow>
        </TableBody>
    </Table>
);


/**
 * Where online requests came from — per source and campaign: how many were
 * placed, how many staff approved, and what the approved orders are worth.
 * Collapsed by default so the request list stays the page's main job.
 * `refreshKey` changes when the list reloads, so the numbers follow.
 */
export const RequestSourcesCard: React.FC<{ refreshKey?: number }> = ({ refreshKey }) => {
    const [days, setDays] = useState<number>(30);
    const [open, setOpen] = useState(false);
    const [data, setData] = useState<RequestSourcesSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError("");
        guestOrderService
            .getSources(days)
            .then((d) => !cancelled && setData(d))
            .catch(() => !cancelled && setError("Couldn't load where requests came from."))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [days, refreshKey]);

    const top = (data?.items ?? []).slice(0, 3);

    return (
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}>
            {loading && <LinearProgress sx={{ height: 2 }} />}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    px: 2,
                    py: 1.25,
                }}
            >
                <Insights fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mr: 0.5 }}>
                    Where requests came from
                </Typography>
                {!open && data && (
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", flex: 1, minWidth: 0 }}>
                        {top.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No requests in this period
                            </Typography>
                        ) : (
                            top.map((g) => (
                                <Chip
                                    key={`${g.source}|${g.campaign ?? ""}`}
                                    size="small"
                                    variant={g.paid ? "filled" : "outlined"}
                                    color={g.paid ? "primary" : "default"}
                                    label={`${g.source}${g.campaign ? ` · ${g.campaign}` : ""}: ${g.requests}`}
                                    sx={{ maxWidth: 260 }}
                                />
                            ))
                        )}
                    </Box>
                )}
                <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={days}
                        onChange={(_, v) => v && setDays(v)}
                        aria-label="Period"
                    >
                        {PERIODS.map((p) => (
                            <ToggleButton key={p} value={p} sx={{ px: 1.25, py: 0.25 }}>
                                {p}d
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <Button
                        size="small"
                        onClick={() => setOpen((o) => !o)}
                        endIcon={open ? <ExpandLess /> : <ExpandMore />}
                    >
                        {open ? "Hide" : "Details"}
                    </Button>
                </Box>
            </Box>

            <Collapse in={open} unmountOnExit>
                {error ? (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {error}
                    </Alert>
                ) : data && data.items.length > 0 ? (
                    <Box sx={{ overflowX: "auto", borderTop: 1, borderColor: "divider" }}>
                        <SourcesTable data={data} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 2, py: 1 }}>
                            Approved value is the orders&apos; current total; cancelled orders aren&apos;t counted. Unpaid bKash attempts are left out.
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1.5 }}>
                        No requests in the last {days} days.
                    </Typography>
                )}
            </Collapse>
        </Paper>
    );
};
